/**
 * Tool SEO Component
 * Adds structured data (JSON-LD) and view tracking for tool pages
 * Use this alongside existing tool page content
 */

'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth/AuthContext';
import { trackToolUsage } from '@/components/RecentlyUsedTools';

interface ToolSEOProps {
  toolName: string;
  toolSlug: string;
  description: string;
  category?: 'calculator' | 'assessment' | 'generator' | 'analyzer' | 'planner';
  isPremium?: boolean;
}

export default function ToolSEO({
  toolName,
  toolSlug,
  description,
  category = 'analyzer',
  isPremium = false,
}: ToolSEOProps) {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();

  // Track tool usage in localStorage (for recently used tools section)
  useEffect(() => {
    if (pathname) {
      trackToolUsage(pathname);
    }
  }, [pathname]);

  // Track view on mount (for authenticated users - server-side tracking)
  useEffect(() => {
    if (isAuthenticated) {
      fetch('/api/secure/recently-viewed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contentType: 'tool',
          contentSlug: toolSlug,
          contentTitle: toolName,
        }),
      }).catch(() => {
        // Silently fail - view tracking is not critical
      });
    }
  }, [isAuthenticated, toolSlug, toolName]);

  // Generate SoftwareApplication JSON-LD schema
  const softwareAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: toolName,
    description: description,
    url: `https://theartofcto.com/tools/${toolSlug}`,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: category.charAt(0).toUpperCase() + category.slice(1),
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: isPremium ? '19.99' : '0',
      priceCurrency: 'USD',
    },
    author: {
      '@type': 'Organization',
      name: 'The Art of CTO',
      url: 'https://theartofcto.com',
    },
  };

  // Generate Breadcrumb JSON-LD schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://theartofcto.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tools',
        item: 'https://theartofcto.com/tools',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: toolName,
        item: `https://theartofcto.com/tools/${toolSlug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}
